import { Box, Link, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { Hr, Img } from 'components/common';
import { Store } from 'rdx';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHref } from 'react-router-dom';
import { BullListItem } from 'types';
import utils from 'utils';
import SelectableButton from '../SelectableButton';
import styles from './BullResultCard.styles';

type Props = {
  bull: BullListItem,
  onClick?: () => void,
  onChange?: (value: boolean) => void,
  showIndices?: boolean
}

const BullResultCard: FC<Props> = ({
  bull,
  onClick,
  onChange,
  showIndices = true
}) => {
  const history = useHref(`${utils.string.printableToPathlike(bull.CRVcode?.toUpperCase())}`);
  const items = useSelector((store: Store) => store.items);
  const [ selected, setSelected ] = useState(false);
  const gtMd = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const indexList = showIndices ? utils.bullIndices.getBullIndicesWithValues(bull) : [];
  const printableIndexes = showIndices && indexList.length > 0
    ? (indexList.map(i => `${i.name}:${i.value}%`).join(' '))
    : '-';
  const computedMainImg = `https://firebasestorage.googleapis.com/v0/b/\
                crv-lagoa.appspot.com/o/\
                ${utils.segmentTranslator(utils.segmentFromBullOrBreed(bull), 'pathlike').replace('-', '_')}\
                %2Fbulls%2F${bull.CRVcode}.jpg?alt=media`
    .replace(/\s/g, '');
  const summaryImageSrc = 'image' in bull
    ? typeof bull.image === 'string' ? bull.image : bull.image?.filepath
    : undefined;
  const mainImgSrc =
    summaryImageSrc ||
    ('images' in bull ? bull.images?.[0]?.filepath : undefined) ||
    ('picture' in bull ? bull.picture : undefined) ||
    computedMainImg;

  useEffect(() => {
    const targetIndex = items.bulls.findIndex(b => b.CRVcode === bull.CRVcode);
    const imperativelySelected = items.selectedBulls.includes(targetIndex);
    setSelected(imperativelySelected);
  }, [ items, bull ]);

  return (
    <Stack
      {
        ...(
          gtMd
            ? utils.stack.flex.row('space-between', 'center')
            : utils.stack.flex.col('center', 'center')
        )
      }
      sx={ styles.container }
    >
      <Link
        { ...utils.stack.flex.row('center', 'center') }
        onClick={(e) => {
          e.preventDefault();
          onClick?.();
        }}
        href={ history }
        sx={ styles.link }
      >
        <Stack
          { ...utils.stack.flex.col('flex-start', 'flex-start') }
          sx={ styles.pictureWrapper }          
        >
          <Stack
            { ...utils.stack.flex.col('center', 'center') }
            sx={ styles.RGDWrapper }
          >
            <Typography>{ bull.CRVcode }</Typography>
          </Stack>
          <Box sx={{ ...utils.sx.flex.col('center', 'center'), ...styles.imageCrop }}>
            <Img
              url={mainImgSrc}
              fallbackImg='/imgs/default.png'
              sx={{ width: '120%', aspectRatio: '3/2' }}
            />
            {
              bull.newRelease === 'X' &&
              <Img 
                url="/imgs/badge-lancamento.svg" 
                sx={{ width: '80px', position: 'relative', top: '-126.5px', left: '-82px', marginTop: '-71px' }}
              />
            }
          </Box>
        </Stack>
        <Stack
          { ...utils.stack.flex.col('flex-start', 'flex-start') }
          spacing={ 1 }
          sx={ styles.infoWrapper }
        >
          <Stack
            {
              ...(
                gtMd
                  ? utils.stack.flex.col('flex-start', 'center')
                  : utils.stack.flex.row('space-between', 'center')
              )
            }
            spacing={ gtMd ? .5 : 0}
            sx={ utils.sx.fw }
          >
            <Typography 
              variant='h3'              
              onClick={ onClick }
              sx={{ cursor: 'pointer' }}
              translate="no"
            >
              { bull?.warName != '-' ?  bull?.warName : bull.name }
            </Typography>
            <Stack { ...utils.stack.flex.row('center', 'center') } spacing={ gtMd ? 2 : .5 }>
              <Img
                url={ utils.segmentIconFromBullOrBreed(bull) }
                sx={{ width: '14px' }}
              />
              <Typography variant='body2'>{ bull.breed }</Typography>
            </Stack>
          </Stack>
          <Stack
            { ...utils.stack.flex.col('flex-start', 'flex-start') }
          >
            <Typography>{ `Registro: ${bull.registry ?? 'S/R'}` }</Typography>
            <Typography>{ `Nascimento: ${bull.birthDate ? utils.string.tsToBrDate(bull.birthDate) : 'S/R'}` }</Typography>
            <Typography>{ `Genealogia: ${bull.lineage ==null || bull.lineage == '-' ? '-': bull.lineage}` }</Typography>
            <Typography>{ `Índices: ${printableIndexes}`}</Typography>

          </Stack>
        </Stack>
      </Link>
      {
        !gtMd &&
        <Hr />
      }
      <Stack
        {
          ...(
            gtMd
              ? utils.stack.flex.col('flex-end', 'center')
              : utils.stack.flex.col('center', 'center')
          )
        }
        sx={ styles.btnWrapper }
      >
        <SelectableButton
          selected={ selected }
          onClick={() => {
            setSelected(v => {
              onChange && onChange(!v);
              return !v;
            });          
          }}
        />
      </Stack>
    </Stack>
  );
};

export default BullResultCard;
